import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import ClientDropdown from '@src/components/ClientDropdown';
import { useMessage } from '@src/context/common';
import paths from '@src/paths';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import {
  assignFacebookPages,
  createFacebookPage,
  fetchFacebookPage,
  fetchFacebookPageSummary,
  fetchPageSetupPreview,
  setupFacebookPage,
  listFacebookPages,
  listPagesToReview,
  refreshFacebookPages,
  updateFacebookPage,
} from '@src/api/facebookPages';

// Meta sets these from a Page's highest spending month since September 2019 and
// only ever raises them. Read from Facebook daily; entered by hand only for a
// Page the sync hasn't reached.
const TIERS = [250, 1000, 5000, 20000];

const VIEWS = [
  { value: 'all', label: 'All Pages' },
  { value: 'review', label: 'Review — Pages with no client, with a guessed client' },
  { value: 'spare', label: 'Spare — no client, no ads running' },
  { value: 'reusable', label: 'Reusable — spare, and no current client matches it' },
  { value: 'restricted', label: 'Restricted — Facebook refuses to read it' },
  { value: 'unassigned', label: 'Unassigned — no client' },
];

const num = (n) => (n || 0).toLocaleString();
const when = (ts) =>
  ts ? new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;

function mismatchText(page) {
  const from = page.ads_mostly_from ? ` Most run from ${page.ads_mostly_from}'s ad accounts.` : '';
  return `Facebook says none of the ${num(page.ads_running)} ads on this Page run from ${
    page.client_name || 'this client'
  }'s ad accounts.${from} Check it's the right Page before publishing under it.`;
}

export default function FacebookPagesPage() {
  const message = useMessage();
  const [searchParams, setSearchParams] = useSearchParams();

  const clientParam = searchParams.get('client');
  const [clientId, setClientId] = useState(clientParam || '');
  const [view, setView] = useState(searchParams.get('view') || 'all');
  const [showInactive, setShowInactive] = useState(false);
  const [pages, setPages] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [settingUp, setSettingUp] = useState(null);

  // The Local Ad Builder links here with ?setup=<page row id> to set a Page up.
  useEffect(() => {
    const id = searchParams.get('setup');
    if (!id) return;
    fetchFacebookPage(id)
      .then(({ data }) => setSettingUp({ ...data, _client: searchParams.get('setup_client') }))
      .catch(() => message.error('Could not open that Page.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, summaryResp] = await Promise.all([
        view === 'review'
          ? listPagesToReview()
          : listFacebookPages({
            client: view === 'all' ? clientId || undefined : view === 'restricted' ? undefined : 'unassigned',
            spare: view === 'spare',
            reusable: view === 'reusable',
            restricted: view === 'restricted',
            includeInactive: showInactive,
          }),
        fetchFacebookPageSummary().catch(() => ({ data: null })),
      ]);
      setPages(Array.isArray(data) ? data : data?.results || []);
      setSummary(summaryResp.data);
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Could not load Pages.');
    } finally {
      setLoading(false);
    }
  }, [clientId, view, showInactive, message]);

  useEffect(() => {
    load();
  }, [load]);

  const selectView = (value) => {
    setView(value);
    const next = new URLSearchParams(searchParams);
    if (value !== 'all') next.set('view', value);
    else next.delete('view');
    setSearchParams(next, { replace: true });
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      if (clientId && view === 'all') {
        const { data } = await refreshFacebookPages({ client_id: clientId });
        message.success(
          data.mismatched
            ? `Read from Facebook. ${data.mismatched} Page(s) look like they belong to another client.`
            : 'Read from Facebook.',
        );
      } else {
        await refreshFacebookPages();
        message.success('Refreshing every Page from Facebook. This takes a few minutes — reload to see it.');
      }
      load();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Could not read Facebook.');
    } finally {
      setRefreshing(false);
    }
  };

  const selectClient = (value) => {
    setClientId(value || '');
    const next = new URLSearchParams(searchParams);
    if (value) next.set('client', value);
    else next.delete('client');
    setSearchParams(next, { replace: true });
  };

  const save = async (page, changes, successText) => {
    try {
      await updateFacebookPage(page.id, changes);
      if (successText) message.success(successText);
      load();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Could not save that change.');
      load();
    }
  };

  return (
    <Stack spacing={3} sx={{ p: 3, maxWidth: 1200 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'flex-end' }}
        spacing={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Facebook Pages
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 680 }}>
            Every ad publishes under a Page, and each Page can only hold so many
            ads. Counts, limits and names are read from Facebook every morning.
            Pages with no client are spare — ready to hand to a client.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            component={RouterLink}
            to={clientId ? `${paths.facebook.localAdBuilder}?client=${clientId}` : paths.facebook.localAdBuilder}
          >
            Local Ad Builder
          </Button>
          <Tooltip
            title={
              clientId && view === 'all'
                ? "Read this client's Pages from Facebook now."
                : 'Read every Page in Business Manager from Facebook. Takes a few minutes.'
            }
          >
            <span>
              <Button onClick={refresh} disabled={refreshing}>
                {refreshing ? 'Reading Facebook…' : 'Refresh from Facebook'}
              </Button>
            </span>
          </Tooltip>
          <Button variant="contained" onClick={() => setAdding(true)}>
            Add Page
          </Button>
        </Stack>
      </Stack>

      {summary && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          {summary.to_review > 0 && (
            <Chip
              color="primary"
              variant={view === 'review' ? 'filled' : 'outlined'}
              label={`${num(summary.to_review)} to review · ${num(summary.suggested)} with a guess`}
              onClick={() => selectView(view === 'review' ? 'all' : 'review')}
            />
          )}
          <Chip
            color="success"
            variant={view === 'spare' ? 'filled' : 'outlined'}
            label={`${num(summary.spare)} spare Page${summary.spare === 1 ? '' : 's'} ready`}
            onClick={() => selectView(view === 'spare' ? 'all' : 'spare')}
          />
          <Tooltip title="Spare, and no current client's name matches it — nothing matches, or only a paused or off client. Safe to rename and reuse.">
            <Chip
              color="success"
              variant={view === 'reusable' ? 'filled' : 'outlined'}
              label={`${num(summary.reusable)} reusable`}
              onClick={() => selectView(view === 'reusable' ? 'all' : 'reusable')}
            />
          </Tooltip>
          {summary.restricted > 0 && (
            <Tooltip title="Facebook refuses to read these — usually restricted, unpublished or removed. Not counted as spare.">
              <Chip
                color="error"
                variant={view === 'restricted' ? 'filled' : 'outlined'}
                label={`${num(summary.restricted)} restricted`}
                onClick={() => selectView(view === 'restricted' ? 'all' : 'restricted')}
              />
            </Tooltip>
          )}
          {summary.mismatched > 0 && (
            <Tooltip title="Pages recorded for a client whose ad accounts run none of the Page's ads.">
              <Chip color="warning" variant="outlined" label={`${num(summary.mismatched)} may be on the wrong client`} />
            </Tooltip>
          )}
          <Typography variant="caption" color="text.secondary">
            {summary.last_synced_at
              ? `Last read from Facebook ${when(summary.last_synced_at)}`
              : 'Not read from Facebook yet — counts are our own estimate until the first refresh.'}
          </Typography>
        </Stack>
      )}

      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              select
              size="small"
              label="Show"
              value={view}
              onChange={(e) => selectView(e.target.value)}
              sx={{ minWidth: 260 }}
            >
              {VIEWS.map((v) => (
                <MenuItem key={v.value} value={v.value}>
                  {v.label}
                </MenuItem>
              ))}
            </TextField>
            {view === 'all' && (
              <Box sx={{ flex: 1, maxWidth: 420 }}>
                <ClientDropdown
                  value={clientId}
                  onChange={(value) => selectClient(value)}
                  label="Client (leave blank for all)"
                />
              </Box>
            )}
            <FormControlLabel
              control={<Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
              label="Show deactivated"
            />
          </Stack>
        </CardContent>
      </Card>

      {view === 'review' ? (
        <ReviewTable pages={pages} loading={loading} message={message} onChanged={load} />
      ) : (
      <Card variant="outlined">
        {loading && <LinearProgress />}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {(!clientId || view !== 'all') && <TableCell>Client</TableCell>}
                <TableCell>Nickname</TableCell>
                <TableCell>Facebook name · Page ID</TableCell>
                <TableCell>
                  <Tooltip title="Meta sets this from the Page's highest spending month since Sept 2019. It only goes up.">
                    <span>Ad limit</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ minWidth: 180 }}>Ads running or in review</TableCell>
                <TableCell>Local ads</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && pages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {['spare', 'reusable'].includes(view)
                        ? 'None. Refresh from Facebook to pick up Pages made in Business Manager.'
                        : clientId
                          ? 'No Pages for this client yet. Add one to let their ads publish.'
                          : 'No Pages yet.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {pages.map((page) => (
                <PageRow
                  key={page.id}
                  page={page}
                  showClient={!clientId || view !== 'all'}
                  onSave={save}
                  onAssign={() => setAssigning(page)}
                  onSetup={() => setSettingUp(page)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      )}

      <AddPageDialog
        open={adding}
        defaultClientId={clientId}
        onClose={() => setAdding(false)}
        onAdded={(created) => {
          setAdding(false);
          message.success(`Added ${created.display_name}.`);
          load();
        }}
      />

      <PageSetupDialog
        page={settingUp}
        message={message}
        onClose={(changed) => {
          setSettingUp(null);
          if (searchParams.get('setup')) {
            const next = new URLSearchParams(searchParams);
            next.delete('setup');
            next.delete('setup_client');
            setSearchParams(next, { replace: true });
          }
          if (changed) load();
        }}
      />

      <ClientChangeDialog
        page={assigning}
        onClose={() => setAssigning(null)}
        onChange={async (client) => {
          const page = assigning;
          setAssigning(null);
          await save(
            page,
            { client },
            client ? `${page.display_name} moved.` : `${page.display_name} is spare again and back in review.`,
          );
        }}
      />
    </Stack>
  );
}

/**
 * Pages with no client, each with the client the sync guessed. Check the guess,
 * change it if it's wrong, and assign — row by row or the checked rows at once.
 * "Keep spare" stops a Page being suggested again.
 */
function ReviewTable({ pages, loading, message, onChanged }) {
  // One client list for every row; the dropdown component would fetch per row.
  // Typing filters it locally — a server search would narrow the list every
  // row shares and blank the other rows' choices.
  const { options, loading: clientsLoading } = useClientAutocomplete();
  const [choice, setChoice] = useState({});
  const [checked, setChecked] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setChoice(Object.fromEntries(pages.map((p) => [p.id, p.suggested_client || ''])));
    setChecked(new Set());
  }, [pages]);

  const optionFor = (page) => {
    const id = choice[page.id];
    if (!id) return null;
    return (
      options.find((o) => String(o.value) === String(id))
      // A guessed client can be inactive, and so missing from the active list.
      || (String(id) === String(page.suggested_client)
        ? { value: page.suggested_client, label: page.suggested_client_name }
        : null)
    );
  };

  const assign = async (ids) => {
    const assignments = ids.filter((id) => choice[id]).map((id) => ({ id, client: choice[id] }));
    if (!assignments.length) return;
    setSaving(true);
    try {
      const { data } = await assignFacebookPages(assignments);
      const skipped = data.skipped?.length ? ` ${data.skipped.length} skipped: ${data.skipped[0].reason}` : '';
      message.success(`Assigned ${data.assigned} Page${data.assigned === 1 ? '' : 's'}.${skipped}`);
      onChanged();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Could not assign those Pages.');
    } finally {
      setSaving(false);
    }
  };

  const keepSpare = async (page) => {
    try {
      await updateFacebookPage(page.id, { suggestion_dismissed: true });
      message.success(`${page.page_name} stays spare.`);
      onChanged();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Could not save that.');
    }
  };

  const toggle = (id) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Only the guesses worth trusting at a glance; "different trade" ones need a look.
  const guessed = pages.filter((p) => p.suggested_client && p.suggestion_confident);
  const readyChecked = [...checked].filter((id) => choice[id]);

  return (
    <Card variant="outlined">
      {(loading || saving) && <LinearProgress />}
      <CardContent sx={{ pb: 0 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
            Guesses come from the ads a Page has run for a client, or from its name. Check each
            one — change the client if it&apos;s wrong — then assign. Nothing is assigned until you do.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => setChecked(new Set(guessed.map((p) => p.id)))} disabled={!guessed.length}>
              Check all {num(guessed.length)} confident guesses
            </Button>
            <Button size="small" variant="contained" disabled={!readyChecked.length || saving} onClick={() => assign(readyChecked)}>
              Assign {num(readyChecked.length)} checked
            </Button>
          </Stack>
        </Stack>
      </CardContent>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Page</TableCell>
              <TableCell>Ads running</TableCell>
              <TableCell sx={{ minWidth: 280 }}>Client</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && pages.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Nothing to review. Every Page has a client or has been kept spare.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {pages.map((page) => (
              <TableRow key={page.id} hover selected={checked.has(page.id)}>
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={checked.has(page.id)} onChange={() => toggle(page.id)} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{page.page_name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {page.page_id}
                    {' · '}
                    <a href={page.ads_library_url} target="_blank" rel="noreferrer">
                      Ads Library
                    </a>
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {page.ads_running === null || page.ads_running === undefined ? '—' : num(page.ads_running)}
                  </Typography>
                  {page.ads_mostly_from && (
                    <Typography variant="caption" color="text.secondary">
                      mostly from {page.ads_mostly_from}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Autocomplete
                    size="small"
                    options={options}
                    loading={clientsLoading}
                    value={optionFor(page)}
                    onChange={(_, option) => setChoice((prev) => ({ ...prev, [page.id]: option?.value || '' }))}
                    isOptionEqualToValue={(option, selected) => String(option.value) === String(selected?.value)}
                    getOptionLabel={(option) => option?.label || ''}
                    renderOption={(props, option) => (
                      <li {...props} key={option.value}>
                        {option.label}
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} placeholder="Choose a client" />}
                  />
                  <Typography
                    variant="caption"
                    color={page.suggested_client && page.suggestion_confident ? 'text.secondary' : 'warning.main'}
                  >
                    {page.suggestion_reason || 'No guess — choose a client, or keep it spare.'}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Button size="small" variant="outlined" disabled={!choice[page.id] || saving} onClick={() => assign([page.id])}>
                    Assign
                  </Button>
                  <Button size="small" color="inherit" onClick={() => keepSpare(page)}>
                    Keep spare
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

/**
 * Give a Page to a client, move it to another, or make it spare again — with the
 * Page's recent changes, so a wrong mapping can be seen and put back.
 */
const FIELD_LABELS = {
  picture: 'Profile picture',
  cover: 'Cover photo',
  about: 'About',
  description: 'Additional info',
  phone: 'Phone',
  emails: 'Email',
  website: 'Website (landing page)',
  category_list: 'Categories',
};

const show = (value) => (Array.isArray(value) ? value.join(', ') : value) || '—';

/**
 * Set a Page up for a client: pick the client, vertical and the Page to copy
 * from, see what changes, choose what to copy, then apply. The name can't be
 * changed through Facebook's API — the dialog links to the Page to do that.
 */
function PageSetupDialog({ page, message, onClose }) {
  const [params, setParams] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState([]);
  const [hidePosts, setHidePosts] = useState(false);
  const [nickname, setNickname] = useState('');
  const [useForLocal, setUseForLocal] = useState(true);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!page) return;
    setParams({ client_id: page._client || page.client || page.suggested_client || '' });
    setData(null);
    setResult(null);
  }, [page]);

  useEffect(() => {
    if (!page || !params.client_id) return;
    let cancelled = false;
    setLoading(true);
    fetchPageSetupPreview(page.id, params)
      .then(({ data: body }) => {
        if (cancelled) return;
        setData(body);
        const changes = body.preview?.changes || [];
        setFields(changes.filter((c) => !c.same && c.has_source).map((c) => c.field));
        setNickname(page.nickname || body.suggested_nickname || '');
        // Hiding posts is for a spare Page taking on a new identity, never a
        // Page with ads running in front of leads (the server refuses that too).
        setHidePosts(Boolean(body.needs_rename) && !(page.ads_running > 0));
      })
      .catch((error) => !cancelled && message.error(error?.response?.data?.detail || 'Could not read Facebook.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [page, params, message]);

  const toggleField = (field) =>
    setFields((prev) => (prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]));

  const apply = async () => {
    setApplying(true);
    try {
      const { data: body } = await setupFacebookPage(page.id, {
        client_id: data.client.id,
        vertical_id: data.vertical_id,
        source_page_id: data.source_page_id,
        fields,
        hide_posts: hidePosts,
        nickname,
        use_for_local: useForLocal,
      });
      setResult(body);
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Setup failed.');
    } finally {
      setApplying(false);
    }
  };

  const changes = data?.preview?.changes || [];
  const target = data?.preview?.target;

  return (
    <Dialog open={Boolean(page)} onClose={() => onClose(Boolean(result))} fullWidth maxWidth="md">
      <DialogTitle>Set up {page?.page_name} for a client</DialogTitle>
      <DialogContent>
        {result ? (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {result.steps.map((step) => (
              <Alert key={step.step} severity={step.ok ? 'success' : 'error'} sx={{ py: 0 }}>
                <b>{FIELD_LABELS[step.step] || step.step.replace(/_/g, ' ')}</b>: {step.detail}
              </Alert>
            ))}
            {result.needs_rename ? (
              <Alert severity="warning">
                Last step: rename the Page to <b>{result.target_name}</b> on Facebook — the API can&apos;t.{' '}
                <a href={result.facebook_url} target="_blank" rel="noreferrer">Open the Page on Facebook</a>, then
                Settings → Page setup → Name. The next sync marks the setup ready.
              </Alert>
            ) : (
              <Alert severity="info">The Page already has the client&apos;s name — nothing to rename.</Alert>
            )}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <ClientDropdown
                  value={params.client_id || ''}
                  onChange={(value) => setParams({ client_id: value || '' })}
                  label="Client"
                />
              </Box>
              <TextField
                select size="small" label="Vertical" sx={{ minWidth: 180 }}
                value={data?.vertical_id || ''}
                disabled={!data}
                onChange={(e) => setParams((prev) => ({ ...prev, vertical_id: e.target.value }))}
              >
                {(data?.verticals || []).map((v) => (
                  <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                ))}
              </TextField>
              <TextField
                select size="small" label="Copy from" sx={{ minWidth: 220 }}
                value={data?.source_page_id || ''}
                disabled={!data}
                onChange={(e) => setParams((prev) => ({ ...prev, source_page_id: e.target.value }))}
              >
                {(data?.sources || []).map((src) => (
                  <MenuItem key={src.page_id} value={src.page_id}>
                    {src.name}{src.ads_running != null ? ` · ${num(src.ads_running)} ads` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            {loading && <LinearProgress />}
            {data && !data.landing_url && (
              <Alert severity="warning">
                No active landing page for this client and vertical, so the website can&apos;t be set.
              </Alert>
            )}
            {data && (
              <Alert severity={data.needs_rename ? 'warning' : 'info'}>
                {data.needs_rename ? (
                  <>
                    Named <b>{page.page_name}</b>; it should be <b>{data.target_name}</b>. The name is changed on
                    Facebook, not here —{' '}
                    <a href={`https://www.facebook.com/${page.page_id}`} target="_blank" rel="noreferrer">open the Page</a>.
                  </>
                ) : (
                  <>Already named <b>{data.target_name}</b>. The nickname below is how we tell it apart.</>
                )}
              </Alert>
            )}
            {changes.length > 0 && (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Field</TableCell>
                      <TableCell>On this Page</TableCell>
                      <TableCell>After</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {changes.map((c) => {
                      const image = c.field === 'picture' || c.field === 'cover';
                      return (
                        <TableRow key={c.field}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={fields.includes(c.field)}
                              disabled={!c.has_source || c.same}
                              onChange={() => toggleField(c.field)}
                            />
                          </TableCell>
                          <TableCell>{FIELD_LABELS[c.field]}</TableCell>
                          <TableCell sx={{ maxWidth: 260, wordBreak: 'break-word' }}>
                            {image ? (
                              target?.[c.field] ? <img src={target[c.field]} alt="" style={{ maxHeight: 48 }} /> : '—'
                            ) : show(c.from)}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 260, wordBreak: 'break-word' }}>
                            {c.same ? (
                              <Typography variant="caption" color="text.secondary">Already the same</Typography>
                            ) : image ? (
                              data.preview.source?.[c.field]
                                ? <img src={data.preview.source[c.field]} alt="" style={{ maxHeight: 48 }} />
                                : '—'
                            ) : show(c.to)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {data && (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <TextField
                  size="small" label="Nickname" value={nickname} sx={{ minWidth: 240 }}
                  onChange={(e) => setNickname(e.target.value)}
                  helperText="How we tell this Page apart — leads never see it."
                  inputProps={{ maxLength: 100 }}
                />
                <FormControlLabel
                  control={<Switch checked={useForLocal} onChange={(e) => setUseForLocal(e.target.checked)} />}
                  label={`Use for ${(data.verticals || []).find((v) => v.id === data.vertical_id)?.name || 'local'} local ads`}
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={hidePosts}
                      disabled={page?.ads_running > 0}
                      onChange={(e) => setHidePosts(e.target.checked)}
                    />
                  )}
                  label={page?.ads_running > 0
                    ? `Hide existing posts — not on a Page with ${num(page.ads_running)} ads running`
                    : `Hide existing posts (${typeof data.preview?.visible_posts === 'number' ? data.preview.visible_posts : '?'})`}
                />
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary">
              Applying writes to the live Page on Facebook. A new profile picture makes Facebook post about it; that
              post is hidden automatically.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(Boolean(result))}>{result ? 'Done' : 'Cancel'}</Button>
        {!result && (
          <Button
            variant="contained"
            disabled={!data || applying || (!fields.length && !hidePosts && !useForLocal && !nickname)}
            onClick={apply}
          >
            {applying ? 'Applying…' : 'Apply to Facebook'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ClientChangeDialog({ page, onClose, onChange }) {
  const [clientId, setClientId] = useState('');

  useEffect(() => {
    if (page) setClientId('');
  }, [page]);

  const history = page?.history || [];
  const current = page?.client_name;

  return (
    <Dialog open={Boolean(page)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{current ? `Change the client for ${page?.display_name}` : `Give ${page?.display_name} to a client`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">
            {current ? <>Belongs to <b>{current}</b>.</> : 'No client — spare.'}
            {page?.ads_running > 0 &&
              ` ${num(page.ads_running)} ads run on it${page.ads_mostly_from ? `, mostly from ${page.ads_mostly_from}` : ''}.`}
          </Typography>
          {current && page?.reserved_for_local && (
            <Alert severity="warning">
              {current}&apos;s local ads use this Page. Moving it clears that choice, and they&apos;ll need
              another Page before publishing.
            </Alert>
          )}
          <ClientDropdown
            value={clientId}
            onChange={(value) => setClientId(value || '')}
            label={current ? 'Move to client' : 'Client'}
          />
          {history.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Recent changes
              </Typography>
              {history.map((h) => (
                <Stack key={`${h.at}-${h.to_id}`} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="caption">
                    {when(h.at)} · {h.from || 'Spare'} → {h.to || 'Spare'} · {h.by || h.source}
                  </Typography>
                  {h.from_id && h.from_id !== page.client && (
                    <Button size="small" onClick={() => onChange(h.from_id)}>
                      Put back to {h.from}
                    </Button>
                  )}
                </Stack>
              ))}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {current && (
          <Button color="inherit" onClick={() => onChange(null)} sx={{ mr: 'auto' }}>
            Make it spare
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!clientId} onClick={() => onChange(clientId)}>
          {current ? 'Move' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PageRow({ page, showClient, onSave, onAssign, onSetup }) {
  const [nickname, setNickname] = useState(page.nickname || '');

  useEffect(() => {
    setNickname(page.nickname || '');
  }, [page.nickname]);

  const commitNickname = () => {
    if (nickname.trim() !== (page.nickname || '')) {
      onSave(page, { nickname: nickname.trim() });
    }
  };

  return (
    <TableRow hover sx={{ opacity: page.active ? 1 : 0.55 }}>
      {showClient && (
        <TableCell>
          {page.client_name ? (
            <Tooltip
              title={
                page.history?.[0]
                  ? `Last changed ${when(page.history[0].at)} by ${page.history[0].by || page.history[0].source}`
                  : ''
              }
            >
              <Typography variant="body2">{page.client_name}</Typography>
            </Tooltip>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              {page.facebook_error ? (
                <Tooltip title={`Facebook refuses to read it: ${page.facebook_error}`}>
                  <Chip size="small" color="error" variant="outlined" label="Restricted" />
                </Tooltip>
              ) : (
                <Chip
                  size="small"
                  color={page.is_spare ? 'success' : 'default'}
                  variant={page.reusable ? 'filled' : 'outlined'}
                  label={page.reusable ? 'Reusable' : page.is_spare ? 'Spare' : 'Unassigned'}
                />
              )}
              <Button size="small" onClick={onAssign}>
                Assign
              </Button>
              {page.suggestion_dismissed && (
                <Tooltip title="It was kept spare in review. Put it back in the review list.">
                  <Button size="small" color="inherit" onClick={() => onSave(page, { suggestion_dismissed: false }, 'Back in review.')}>
                    Review again
                  </Button>
                </Tooltip>
              )}
            </Stack>
          )}
        </TableCell>
      )}
      <TableCell sx={{ minWidth: 200 }}>
        <TextField
          size="small"
          placeholder="Add a nickname"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          onBlur={commitNickname}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          inputProps={{ maxLength: 100, 'aria-label': 'Nickname' }}
          fullWidth
        />
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">{page.page_name}</Typography>
          {page.client_mismatch && (
            <Tooltip title={mismatchText(page)}>
              <Chip size="small" color="warning" label="Wrong client?" />
            </Tooltip>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {page.page_id}
          {' · '}
          <Tooltip title="Open the Page on Facebook — the name is changed there, not through the API.">
            <a href={page.facebook_url} target="_blank" rel="noreferrer">
              Open on Facebook
            </a>
          </Tooltip>
          {' · '}
          <a href={page.ads_library_url} target="_blank" rel="noreferrer">
            Ads Library
          </a>
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
          {page.reserved_for_local && (
            <Tooltip title={(page.local_vertical_names || []).length > 1 ? 'Carries more than one vertical’s local ads — leads see both on this Page.' : ''}>
              <Chip
                size="small"
                color={(page.local_vertical_names || []).length > 1 ? 'warning' : 'primary'}
                variant="outlined"
                label={(page.local_vertical_names || []).length
                  ? `Local — ${page.local_vertical_names.join(' + ')}`
                  : 'Local'}
              />
            </Tooltip>
          )}
          {page.latest_setup?.status === 'awaiting_rename' && (
            <Tooltip title={`Set up for the client; rename it to "${page.latest_setup.target_name}" on Facebook. The next sync marks it ready.`}>
              <Chip size="small" color="warning" label="Rename on Facebook" component="a" clickable
                href={page.facebook_url} target="_blank" rel="noreferrer" />
            </Tooltip>
          )}
        </Stack>
      </TableCell>
      <TableCell>
        {page.volume_synced_at ? (
          <Tooltip title="Read from Facebook.">
            <Typography variant="body2">{num(page.ad_tier)}</Typography>
          </Tooltip>
        ) : (
          <TextField
            select
            size="small"
            value={page.ad_tier}
            onChange={(e) => onSave(page, { ad_tier: Number(e.target.value) }, 'Ad limit updated.')}
            inputProps={{ 'aria-label': 'Ad limit' }}
            sx={{ minWidth: 100 }}
          >
            {TIERS.map((t) => (
              <MenuItem key={t} value={t}>
                {num(t)}
              </MenuItem>
            ))}
          </TextField>
        )}
      </TableCell>
      <TableCell>
        <Stack direction="row" justifyContent="space-between">
          <Tooltip
            title={
              page.volume_synced_at
                ? `Facebook's count, read ${when(page.volume_synced_at)}. Includes ads from every ad account.`
                : "Our own count of this Page's live ads — Facebook hasn't been read yet, so it can be low."
            }
          >
            <Typography variant="caption" color="text.secondary">
              {num(page.ads_used)} / {num(page.ad_tier)}
              {page.volume_synced_at ? '' : ' (estimate)'}
            </Typography>
          </Tooltip>
          {page.capacity_status !== 'ok' && (
            <Typography
              variant="caption"
              color={page.capacity_status === 'full' ? 'error.main' : 'warning.main'}
              fontWeight={600}
            >
              {page.capacity_status === 'full' ? 'Full' : `${Math.round(page.usage_pct)}%`}
            </Typography>
          )}
        </Stack>
        <LinearProgress
          variant="determinate"
          value={Math.min(page.usage_pct, 100)}
          color={page.capacity_status === 'full' ? 'error' : page.capacity_status === 'warning' ? 'warning' : 'primary'}
          sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
        />
      </TableCell>
      <TableCell>
        {page.reserved_for_local ? (
          <Tooltip title="Change this in the Local Ad Builder.">
            <Chip size="small" color="primary" label="Used for local" />
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        {!page.facebook_error && page.active && (
          <Tooltip title="Copy the client's main Page profile onto this Page and point its website at their landing page.">
            <Button size="small" onClick={onSetup}>
              Set up for client
            </Button>
          </Tooltip>
        )}
        {page.client_name && (
          <Button size="small" onClick={onAssign}>
            Change client
          </Button>
        )}
        {page.active ? (
          <Tooltip
            title={
              page.reserved_for_local
                ? "This is the Page local ads use. Deactivating it clears that choice, and you'll need to pick another before publishing."
                : 'Hides this Page everywhere. Ads already on it are unaffected.'
            }
          >
            <Button size="small" color="inherit" onClick={() => onSave(page, { active: false }, 'Page deactivated.')}>
              Deactivate
            </Button>
          </Tooltip>
        ) : (
          <Button size="small" onClick={() => onSave(page, { active: true }, 'Page restored.')}>
            Restore
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

const EMPTY = { page_id: '', page_name: '', nickname: '', ad_tier: 1000 };

// The client field is optional: a Page with none is recorded as spare.

function AddPageDialog({ open, defaultClientId, onClose, onAdded }) {
  const [clientId, setClientId] = useState(defaultClientId || '');
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setClientId(defaultClientId || '');
      setForm(EMPTY);
      setErrors({});
    }
  }, [open, defaultClientId]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async () => {
    setSaving(true);
    setErrors({});
    try {
      const { data } = await createFacebookPage({ ...form, client: clientId || null });
      onAdded(data);
    } catch (error) {
      const body = error?.response?.data || {};
      setErrors(
        body.detail ? { detail: body.detail } : Object.fromEntries(
          Object.entries(body).map(([k, v]) => [k, Array.isArray(v) ? v.join(' ') : String(v)]),
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const ready = form.page_id.trim() && form.page_name.trim();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add a Facebook Page</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errors.detail && <Alert severity="error">{errors.detail}</Alert>}
          <ClientDropdown
            value={clientId}
            onChange={(value) => setClientId(value || '')}
            label="Client (leave empty for a spare Page)"
          />
          <TextField
            label="Page ID"
            value={form.page_id}
            onChange={set('page_id')}
            error={Boolean(errors.page_id)}
            helperText={
              errors.page_id
              || 'The long number from the Page’s About section, or from view_all_page_id= in its Ads Library link.'
            }
            inputProps={{ inputMode: 'numeric' }}
          />
          <TextField
            label="Facebook name"
            value={form.page_name}
            onChange={set('page_name')}
            error={Boolean(errors.page_name)}
            helperText={errors.page_name || 'As it appears on Facebook.'}
          />
          <TextField
            label="Nickname (optional)"
            value={form.nickname}
            onChange={set('nickname')}
            helperText="Something your team will recognise, e.g. “Local — Texas”."
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            select
            label="Ad limit"
            value={form.ad_tier}
            onChange={set('ad_tier')}
            helperText="Replaced by Facebook's figure at the next refresh. Leave at 1,000 if unsure."
          >
            {TIERS.map((t) => (
              <MenuItem key={t} value={t}>
                {num(t)} ads
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!ready || saving} onClick={submit}>
          Add Page
        </Button>
      </DialogActions>
    </Dialog>
  );
}
